import { CASES } from "./constants";

/** Storage key prefix for persisting filters per offer */
const OFFER_HISTORY_FILTERS_STORAGE_KEY_PREFIX = "offerHistoryFilters-";

/**
 * Get localStorage key for offer history filters by offer ID
 * @param {string|number} offerId - The offer ID
 * @returns {string} The localStorage key
 */
const getOfferHistoryFiltersKey = (offerId) =>
  `${OFFER_HISTORY_FILTERS_STORAGE_KEY_PREFIX}${offerId}`;

/**
 * Load saved filters from localStorage for a specific offer
 * @param {string|number} offerId - The offer ID
 * @returns {Object} The saved filters or empty object
 */
export const loadSavedFilters = (offerId) => {
  if (!offerId) return {};
  try {
    const saved = localStorage.getItem(getOfferHistoryFiltersKey(offerId));
    return saved ? JSON.parse(saved) : {};
  } catch (e) {
    console.error("Failed to load saved offer history filters:", e);
    return {};
  }
};

/**
 * Save filters to localStorage for a specific offer
 * @param {string|number} offerId - The offer ID
 * @param {Object} filters - The filters to save
 */
const saveFiltersToStorage = (offerId, filters) => {
  if (!offerId) return;
  try {
    if (Object.keys(filters).length > 0) {
      localStorage.setItem(
        getOfferHistoryFiltersKey(offerId),
        JSON.stringify(filters),
      );
    } else {
      localStorage.removeItem(getOfferHistoryFiltersKey(offerId));
    }
  } catch (e) {
    console.error("Failed to save offer history filters:", e);
  }
};

/**
 * Remove filters from localStorage for a specific offer
 * @param {string|number} offerId - The offer ID
 */
const removeFiltersFromStorage = (offerId) => {
  if (!offerId) return;
  try {
    localStorage.removeItem(getOfferHistoryFiltersKey(offerId));
  } catch (e) {
    console.error("Failed to remove offer history filters:", e);
  }
};

/**
 * Offer History Redux State Shape
 * @typedef {Object} OfferHistoryState
 * @property {Object} internalFilters - Active filter values from filter drawer
 * @property {string} searchAll - Global search text
 */
const initialState = {
  internalFilters: {},
  searchAll: "",
};

export function offerHistory(state = initialState, action) {
  const { type, payload } = action;

  switch (type) {
    case CASES.UPDATE_OFFER_HISTORY: {
      return { ...state, ...payload };
    }
    case CASES.UPDATE_OFFER_HISTORY_FILTERS: {
      // Merge new filter values into existing filters and persist
      const { offerId, ...filterUpdate } = payload;
      const newFilters = { ...state.internalFilters, ...filterUpdate };
      if (offerId) {
        saveFiltersToStorage(offerId, newFilters);
      }
      return {
        ...state,
        internalFilters: newFilters,
      };
    }
    case CASES.SET_OFFER_HISTORY_FILTERS: {
      // Replace all filters (used for bulk updates like removing a filter)
      const { filters, offerId } = payload;
      if (offerId) {
        saveFiltersToStorage(offerId, filters);
      }
      return {
        ...state,
        internalFilters: filters,
      };
    }
    case CASES.LOAD_OFFER_HISTORY_FILTERS: {
      // Load filters from localStorage for a specific offer
      const { offerId } = payload;
      const savedFilters = loadSavedFilters(offerId);
      return {
        ...state,
        internalFilters: savedFilters,
      };
    }
    case CASES.CLEAR_OFFER_HISTORY_FILTERS: {
      // Clear filters and remove from localStorage
      const { offerId } = payload;
      if (offerId) {
        removeFiltersFromStorage(offerId);
      }
      return {
        ...state,
        internalFilters: {},
        searchAll: "",
      };
    }
    default:
      return state;
  }
}
